import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getInventory } from '@/lib/checkoutClient';
import type { InventoryResponse } from '@/types';
import { isMockMode, getMockInventory } from '@/lib/mock-data';

/**
 * useInventory — polls api-taquilla `/v1/events/:eventId/inventory`
 * every 5 seconds. Cancels polling automatically when the page unmounts.
 *
 * Q8 RESPONSE: edge function dedicada (Opción A) confirmada por admin.
 * Shape: ver `InventoryResponse` en `src/types/taquilla.types.ts`.
 *
 * Polling cadence per kickoff § 3: "polling cada 5s para % vendido y
 * order.status. Supabase Realtime websocket queda para post-H7 buffer."
 */
export function useInventory(eventId: string | undefined): UseQueryResult<InventoryResponse> {
  return useQuery({
    queryKey: ['inventory', eventId, isMockMode() ? 'mock' : 'live'],
    queryFn: async () => {
      if (!eventId) throw new Error('useInventory: eventId is required');
      if (isMockMode()) {
        // Skip the 5s polling visual jitter on the very first call.
        await new Promise((r) => setTimeout(r, 80));
        const mock = getMockInventory(eventId);
        if (!mock) {
          throw Object.assign(new Error('Mock inventory not found'), {
            status: 404,
            code: 'EVENT_NOT_FOUND',
          });
        }
        return mock;
      }
      return getInventory(eventId);
    },
    enabled: !!eventId,
    // Mock mode doesn't need polling — but keep the interval consistent so the
    // UI displays the "actualizado cada 5s" badge identically across modes.
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
